import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/shared/lib/utils'
import { Label } from '@/shared/ui/label'
import { Separator } from '@/shared/ui/separator'

/**
 * shadcn Field primitives — library-agnostic.
 * El campo (Field/FieldLabel/FieldError) no depende de ninguna librería de forms:
 * sólo la línea del Controller cambia si se migra de RHF a TanStack Form.
 */

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn('flex min-w-0 max-w-full flex-col gap-6', className)}
      {...props}
    />
  )
}

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        'mb-4 w-full border-b border-border/70 pb-2 font-semibold text-primary data-[variant=legend]:text-base data-[variant=label]:text-[0.95rem]',
        className
      )}
      {...props}
    />
  )
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn('flex w-full flex-col gap-7', className)}
      {...props}
    />
  )
}

const fieldVariants = cva(
  'group/field flex w-full min-w-0 max-w-full data-[invalid=true]:text-destructive',
  {
    variants: {
      orientation: {
        // Vertical: label e input pegados (como los forms legacy, gap-1). El gap-3 anterior
        // los separaba demasiado. Horizontal conserva el aire lateral entre label y control.
        vertical: 'flex-col gap-1.5 [&>*]:w-full',
        horizontal: 'flex-row items-center gap-3 [&>[data-slot=field-label]]:flex-auto'
      }
    },
    defaultVariants: { orientation: 'vertical' }
  }
)

function Field({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  )
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn('flex min-w-0 flex-1 flex-col gap-1.5 leading-snug', className)}
      {...props}
    />
  )
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        'block w-full text-xs font-semibold leading-snug text-primary group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    />
  )
}

function OptionalHint({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="field-optional-hint"
      className={cn('ml-1 whitespace-nowrap font-normal text-muted-foreground', className)}
      {...props}
    >
      (opcional)
    </span>
  )
}

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-title"
      className={cn('flex w-fit items-center gap-2 text-sm font-medium leading-snug', className)}
      {...props}
    />
  )
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn('text-muted-foreground text-xs font-normal leading-normal', className)}
      {...props}
    />
  )
}

function FieldSeparator({ children, className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn('relative -my-2 h-5 text-sm', className)}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span className="bg-background text-muted-foreground relative mx-auto block w-fit px-2">
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  const content = React.useMemo(() => {
    if (children) return children
    if (!errors) return null
    const filtered = errors.filter((error) => error?.message)
    if (filtered.length === 0) return null
    if (filtered.length === 1) return filtered[0]?.message
    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {filtered.map((error, index) => (
          <li key={index}>{error?.message}</li>
        ))}
      </ul>
    )
  }, [children, errors])

  if (!content) return null

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn('text-xs font-normal text-red-500', className)}
      {...props}
    >
      {content}
    </div>
  )
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  OptionalHint
}
